import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FolderTree, Plus, Edit2, Trash2, Loader2, ChevronRight, CornerDownRight,
  Search, Layers, ExternalLink, Image as ImageIcon, Sparkles, Check, X,
  AlertCircle, ArrowUpRight, List, GitFork
} from 'lucide-react';
import { apiFetch } from '../lib/auth';
import type { Category } from '@zarin/types';

interface CategoryFormState {
  id?: string;
  title: string;
  handle: string;
  parentId: string;
  description: string;
  imageUrl: string;
  sortOrder: number;
  seoTitle: string;
  seoDescription: string;
}

export interface TreeNode extends Category {
  level: number;
  children: TreeNode[];
}

const initialFormState: CategoryFormState = {
  title: '',
  handle: '',
  parentId: '',
  description: '',
  imageUrl: '',
  sortOrder: 0,
  seoTitle: '',
  seoDescription: '',
};

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [viewMode, setViewMode]     = useState<'tree' | 'table'>('tree');
  
  // Modal state
  const [modalOpen, setModalOpen]     = useState(false);
  const [editingCat, setEditingCat]   = useState<Category | null>(null);
  const [form, setForm]               = useState<CategoryFormState>(initialFormState);
  const [submitting, setSubmitting]   = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Load flat categories and tree
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ data: Category[] }>('/categories');
      setCategories(res.data || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Compute category levels and tree map
  const categoryMap = useMemo(() => {
    const map = new Map<string, Category>();
    categories.forEach(c => map.set(c.id, c));
    return map;
  }, [categories]);

  const getLevel = useCallback((cat: Category): number => {
    let level = 1;
    let cur = cat.parentId;
    const visited = new Set<string>();
    while (cur && level <= 4) {
      if (visited.has(cur)) break;
      visited.add(cur);
      const parent = categoryMap.get(cur);
      if (!parent) break;
      level++;
      cur = parent.parentId;
    }
    return level;
  }, [categoryMap]);

  // Hierarchy stats
  const stats = useMemo(() => {
    let l1 = 0;
    let l2 = 0;
    let l3 = 0;
    categories.forEach(cat => {
      const lvl = getLevel(cat);
      if (lvl === 1) l1++;
      else if (lvl === 2) l2++;
      else if (lvl >= 3) l3++;
    });
    return { total: categories.length, l1, l2, l3 };
  }, [categories, getLevel]);

  // Filtered categories
  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;
    const q = search.toLowerCase();
    return categories.filter(c =>
      c.title.toLowerCase().includes(q) ||
      c.handle.toLowerCase().includes(q) ||
      (c.description && c.description.toLowerCase().includes(q))
    );
  }, [categories, search]);

  // Hierarchical tree structure
  const categoryTree = useMemo((): TreeNode[] => {
    const nodeMap = new Map<string, TreeNode>();
    categories.forEach(c => {
      nodeMap.set(c.id, { ...c, level: getLevel(c), children: [] });
    });

    const roots: TreeNode[] = [];
    nodeMap.forEach(node => {
      if (node.parentId && nodeMap.has(node.parentId)) {
        nodeMap.get(node.parentId)!.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Sort nodes by sortOrder then title
    const sortNodes = (nodes: TreeNode[]) => {
      nodes.sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
      nodes.forEach(n => sortNodes(n.children));
    };
    sortNodes(roots);

    return roots;
  }, [categories, getLevel]);

  // Open Create Modal
  const handleOpenCreate = (prefillParentId?: string) => {
    setEditingCat(null);
    setForm({
      ...initialFormState,
      parentId: prefillParentId || '',
    });
    setErrorMessage(null);
    setModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (cat: Category) => {
    setEditingCat(cat);
    setForm({
      id: cat.id,
      title: cat.title,
      handle: cat.handle,
      parentId: cat.parentId || '',
      description: cat.description || '',
      imageUrl: cat.imageUrl || '',
      sortOrder: cat.sortOrder || 0,
      seoTitle: cat.seoTitle || '',
      seoDescription: cat.seoDescription || '',
    });
    setErrorMessage(null);
    setModalOpen(true);
  };

  // Handle Form Submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setErrorMessage('Category title is required');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    const rawHandle = form.handle.trim() || form.title;
    const cleanHandle = slugify(rawHandle);

    const payload = {
      title: form.title.trim(),
      handle: cleanHandle || undefined,
      parentId: form.parentId ? form.parentId : null,
      description: form.description.trim() || null,
      imageUrl: form.imageUrl.trim() || null,
      sortOrder: Number(form.sortOrder) || 0,
      seoTitle: form.seoTitle.trim() || null,
      seoDescription: form.seoDescription.trim() || null,
    };

    try {
      if (editingCat) {
        await apiFetch(`/categories/${editingCat.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch('/categories', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      setModalOpen(false);
      load();
    } catch (err: any) {
      console.error('Failed to save category:', err);
      setErrorMessage(err?.detail || err?.title || err?.message || 'Failed to save category. Please check parent depth.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Delete
  const handleDelete = async (cat: Category) => {
    const hasChildren = categories.some(c => c.parentId === cat.id);
    const confirmText = hasChildren
      ? `"${cat.title}" has subcategories. Deleting it will re-parent its subcategories to its parent. Continue?`
      : `Are you sure you want to delete category "${cat.title}"?`;

    if (!confirm(confirmText)) return;

    try {
      await apiFetch(`/categories/${cat.id}`, { method: 'DELETE' });
      load();
    } catch (err: any) {
      alert(err?.detail || err?.title || err?.message || 'Failed to delete category');
    }
  };

  // Compute breadcrumb path preview for modal
  const modalBreadcrumbs = useMemo(() => {
    if (!form.parentId) return ['Root (Department Level 1)'];
    const crumbs: string[] = [];
    let cur: string | null = form.parentId;
    const visited = new Set<string>();
    while (cur) {
      if (visited.has(cur)) break;
      visited.add(cur);
      const parent = categoryMap.get(cur);
      if (!parent) break;
      crumbs.unshift(parent.title);
      cur = parent.parentId;
    }
    return [...crumbs, form.title || 'New Category'];
  }, [form.parentId, form.title, categoryMap]);

  return (
    <div style={styles.page}>
      {/* Top Header */}
      <div style={styles.header}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h1 style={styles.title}>Categories & Departments</h1>
            <span style={styles.levelTag}>3-Level Tree</span>
          </div>
          <p style={styles.subtitle}>
            Organize the catalog into Departments, Categories, and Subcategories to drive the mega-menu and breadcrumbs.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={styles.viewToggleGroup}>
            <button
              onClick={() => setViewMode('tree')}
              style={{ ...styles.viewToggleBtn, ...(viewMode === 'tree' ? styles.viewToggleActive : {}) }}
              title="Hierarchical Tree View"
            >
              <GitFork size={15} />
              <span>Tree</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              style={{ ...styles.viewToggleBtn, ...(viewMode === 'table' ? styles.viewToggleActive : {}) }}
              title="Table View"
            >
              <List size={15} />
              <span>Table</span>
            </button>
          </div>

          <button style={styles.btnPrimary} onClick={() => handleOpenCreate()}>
            <Plus size={16} />
            <span>Create Category</span>
          </button>
        </div>
      </div>

      {/* Stats Summary Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Total Categories</span>
          <span style={styles.statValue}>{stats.total}</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Level 1 Departments</span>
          <span style={styles.statValue}>{stats.l1}</span>
          <span style={styles.statSub}>Root level</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Level 2 Categories</span>
          <span style={styles.statValue}>{stats.l2}</span>
          <span style={styles.statSub}>Mid-tier categories</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Level 3 Subcategories</span>
          <span style={styles.statValue}>{stats.l3}</span>
          <span style={styles.statSub}>Leaves</span>
        </div>
      </div>

      {/* Toolbar / Search */}
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={16} color="#777" />
          <input
            type="text"
            placeholder="Search categories by title, handle, or description..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={styles.searchInput}
          />
          {search && (
            <button onClick={() => setSearch('')} style={styles.clearSearchBtn}>
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div style={styles.loadingBox}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#C9A84C' }} />
          <span style={{ color: '#9B9B9B', fontSize: '0.875rem' }}>Loading catalog categories...</span>
        </div>
      ) : categories.length === 0 ? (
        <div style={styles.emptyState}>
          <FolderTree size={42} strokeWidth={1.5} color="#555" />
          <h3 style={styles.emptyTitle}>No categories created yet</h3>
          <p style={styles.emptySubtitle}>
            Create your first department (e.g. Women, Men, Accessories) to begin organizing products and building the storefront menu.
          </p>
          <button style={styles.btnPrimary} onClick={() => handleOpenCreate()}>
            <Plus size={16} />
            <span>Create First Department</span>
          </button>
        </div>
      ) : viewMode === 'tree' ? (
        /* ── Tree View ── */
        <div style={styles.treeContainer}>
          <div style={styles.treeHeader}>
            <span>Department Hierarchy (3 Levels)</span>
            <span style={{ color: '#888', fontSize: '0.8125rem' }}>
              Level 1 (Department) → Level 2 (Category) → Level 3 (Subcategory)
            </span>
          </div>

          <div style={styles.treeList}>
            {categoryTree.map(rootNode => (
              <TreeRow
                key={rootNode.id}
                node={rootNode}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
                onAddSub={handleOpenCreate}
                searchQuery={search}
              />
            ))}
          </div>
        </div>
      ) : (
        /* ── Table View ── */
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Level</th>
                <th style={styles.th}>Handle</th>
                <th style={styles.th}>Parent</th>
                <th style={styles.th}>Products</th>
                <th style={styles.th}>Sort Order</th>
                <th style={styles.thRight}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.map(cat => {
                const level = getLevel(cat);
                return (
                  <tr key={cat.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {cat.imageUrl ? (
                          <img src={cat.imageUrl} alt="" style={styles.catThumb} />
                        ) : (
                          <div style={styles.catThumbPlaceholder}>
                            <FolderTree size={14} color="#888" />
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 600, color: '#FAF7F2' }}>{cat.title}</div>
                          {cat.description && (
                            <div style={{ fontSize: '0.75rem', color: '#888', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {cat.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={level === 1 ? styles.badgeL1 : level === 2 ? styles.badgeL2 : styles.badgeL3}>
                        Level {level} {level === 1 ? 'Dept' : level === 2 ? 'Cat' : 'Sub'}
                      </span>
                    </td>
                    <td style={styles.tdMono}>/{cat.handle}</td>
                    <td style={styles.tdMuted}>{cat.parentTitle || '— (Root)'}</td>
                    <td style={styles.td}>
                      <span style={styles.productCountBadge}>{cat.productCount ?? 0}</span>
                    </td>
                    <td style={styles.tdMono}>{cat.sortOrder}</td>
                    <td style={styles.tdRight}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.35rem' }}>
                        {level < 3 && (
                          <button
                            onClick={() => handleOpenCreate(cat.id)}
                            style={styles.iconBtn}
                            title="Add subcategory"
                          >
                            <Plus size={15} />
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenEdit(cat)}
                          style={styles.iconBtn}
                          title="Edit category"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          onClick={() => handleDelete(cat)}
                          style={{ ...styles.iconBtn, color: '#EF4444' }}
                          title="Delete category"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Category Modal ── */}
      {modalOpen && (
        <div style={styles.modalOverlay} onClick={() => setModalOpen(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>
                  {editingCat ? `Edit Category: ${editingCat.title}` : 'Create New Category'}
                </h2>
                <div style={styles.breadcrumbPreview}>
                  <span style={{ color: '#888' }}>Path: </span>
                  {modalBreadcrumbs.map((crumb, idx) => (
                    <span key={idx} style={{ display: 'inline-flex', alignItems: 'center' }}>
                      {idx > 0 && <ChevronRight size={12} style={{ margin: '0 4px', color: '#666' }} />}
                      <span style={{ color: idx === modalBreadcrumbs.length - 1 ? '#C9A84C' : '#FAF7F2', fontWeight: 500 }}>
                        {crumb}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
              <button onClick={() => setModalOpen(false)} style={styles.modalCloseBtn}>
                <X size={18} />
              </button>
            </div>

            {errorMessage && (
              <div style={styles.errorAlert}>
                <AlertCircle size={16} />
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: 2 }}>
                  <label style={styles.label}>
                    Category Title <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Outerwear, Silk Tops, Minimalist Blazers"
                    value={form.title}
                    onChange={e => {
                      const title = e.target.value;
                      setForm(prev => ({
                        ...prev,
                        title,
                        handle: editingCat ? prev.handle : slugify(title),
                      }));
                    }}
                    style={styles.input}
                    autoFocus
                  />
                </div>

                <div style={{ ...styles.formGroup, flex: 1.5 }}>
                  <label style={styles.label}>Slug / URL Handle</label>
                  <input
                    type="text"
                    placeholder="e.g. outerwear"
                    value={form.handle}
                    onChange={e => setForm(prev => ({ ...prev, handle: slugify(e.target.value) }))}
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Parent Category Selector */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Parent Category (Hierarchy: Max 3 Levels)
                </label>
                <select
                  value={form.parentId}
                  onChange={e => setForm(prev => ({ ...prev, parentId: e.target.value }))}
                  style={styles.select}
                >
                  <option value="">None (Level 1: Root Department)</option>
                  {categories
                    .filter(c => !editingCat || c.id !== editingCat.id)
                    .map(c => {
                      const lvl = getLevel(c);
                      const isTooDeep = lvl >= 3;
                      return (
                        <option
                          key={c.id}
                          value={c.id}
                          disabled={isTooDeep}
                        >
                          {lvl === 1 ? 'L1: ' : '↳ L2: '} {c.title} {isTooDeep ? '(Max depth reached)' : ''}
                        </option>
                      );
                    })}
                </select>
                <span style={styles.helperText}>
                  Choose "None" to create a Department (e.g. Women, Men, Home), or nest within an existing category.
                </span>
              </div>

              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: 2 }}>
                  <label style={styles.label}>Image URL (Hero / Thumbnail)</label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={form.imageUrl}
                    onChange={e => setForm(prev => ({ ...prev, imageUrl: e.target.value }))}
                    style={styles.input}
                  />
                </div>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>Sort Order</label>
                  <input
                    type="number"
                    value={form.sortOrder}
                    onChange={e => setForm(prev => ({ ...prev, sortOrder: parseInt(e.target.value, 10) || 0 }))}
                    style={styles.input}
                  />
                </div>
              </div>

              {form.imageUrl && (
                <div style={styles.imagePreviewBox}>
                  <img
                    src={form.imageUrl}
                    alt="Preview"
                    style={styles.imagePreview}
                    onError={e => { (e.target as HTMLElement).style.display = 'none'; }}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#9B9B9B' }}>Live image preview</span>
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  rows={3}
                  placeholder="Editorial description of the category..."
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  style={styles.textarea}
                />
              </div>

              {/* SEO Section */}
              <div style={styles.seoSection}>
                <span style={styles.seoTitleLabel}>SEO Metadata</span>
                <div style={styles.formRow}>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>SEO Title</label>
                    <input
                      type="text"
                      placeholder="e.g. Luxury Wool & Silk Outerwear | Zarin Wear"
                      value={form.seoTitle}
                      onChange={e => setForm(prev => ({ ...prev, seoTitle: e.target.value }))}
                      style={styles.input}
                    />
                  </div>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>SEO Description</label>
                    <input
                      type="text"
                      placeholder="Search engine summary..."
                      value={form.seoDescription}
                      onChange={e => setForm(prev => ({ ...prev, seoDescription: e.target.value }))}
                      style={styles.input}
                    />
                  </div>
                </div>
              </div>

              <div style={styles.modalButtons}>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  style={styles.btnSecondary}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={styles.btnPrimary}
                  disabled={submitting}
                >
                  {submitting && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                  <span>{editingCat ? 'Save Changes' : 'Create Category'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tree Row Component for Recursive Hierarchy ─────────────
interface TreeRowProps {
  node: TreeNode;
  onEdit: (cat: Category) => void;
  onDelete: (cat: Category) => void;
  onAddSub: (parentId: string) => void;
  searchQuery: string;
}

function TreeRow({ node, onEdit, onDelete, onAddSub, searchQuery }: TreeRowProps) {
  const [collapsed, setCollapsed] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const level = node.level;

  return (
    <div style={styles.treeNodeGroup}>
      <div
        style={{
          ...styles.treeRow,
          paddingLeft: `${(level - 1) * 2.25 + 1}rem`,
          background: level === 1 ? '#1A1A1A' : level === 2 ? '#1E1E1E' : '#222222',
        }}
      >
        <div style={styles.treeRowLeft}>
          {hasChildren ? (
            <button
              onClick={() => setCollapsed(!collapsed)}
              style={styles.collapseToggle}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              <ChevronRight
                size={14}
                style={{
                  transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                  transition: 'transform 0.15s ease',
                  color: '#C9A84C',
                }}
              />
            </button>
          ) : (
            <div style={{ width: 22, display: 'inline-flex', justifyContent: 'center' }}>
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#555' }} />
            </div>
          )}

          {node.imageUrl ? (
            <img src={node.imageUrl} alt="" style={styles.catThumb} />
          ) : (
            <div style={styles.catThumbPlaceholder}>
              <FolderTree size={14} color="#888" />
            </div>
          )}

          <div style={styles.treeTitleBlock}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={styles.treeTitleText}>{node.title}</span>
              <span style={level === 1 ? styles.badgeL1 : level === 2 ? styles.badgeL2 : styles.badgeL3}>
                L{level} {level === 1 ? 'Dept' : level === 2 ? 'Cat' : 'Sub'}
              </span>
            </div>
            <span style={styles.treeHandleText}>/{node.handle}</span>
          </div>
        </div>

        <div style={styles.treeRowRight}>
          <div style={styles.productCountBadge} title="Linked products">
            {node.productCount ?? 0} {node.productCount === 1 ? 'item' : 'items'}
          </div>

          <div style={styles.treeActions}>
            {level < 3 && (
              <button
                onClick={() => onAddSub(node.id)}
                style={styles.treeActionBtn}
                title={`Add subcategory inside ${node.title}`}
              >
                <Plus size={14} />
                <span style={{ fontSize: '0.75rem' }}>Add Sub</span>
              </button>
            )}
            <button
              onClick={() => onEdit(node)}
              style={styles.iconBtn}
              title="Edit category"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => onDelete(node)}
              style={{ ...styles.iconBtn, color: '#EF4444' }}
              title="Delete category"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>

      {!collapsed && hasChildren && (
        <div style={styles.treeChildren}>
          {node.children.map(child => (
            <TreeRow
              key={child.id}
              node={child}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddSub={onAddSub}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: '1.25rem', width: '100%', maxWidth: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' },
  title: { fontSize: '1.75rem', fontWeight: 600, color: '#FAF7F2', margin: 0 },
  subtitle: { fontSize: '0.875rem', color: '#9B9B9B', margin: '0.35rem 0 0 0' },
  levelTag: {
    background: 'rgba(201, 168, 76, 0.15)',
    border: '1px solid #C9A84C',
    color: '#C9A84C',
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '0.15rem 0.5rem',
    borderRadius: '4px',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  btnPrimary: {
    background: '#C9A84C',
    color: '#1A1A1A',
    border: 'none',
    padding: '0.55rem 1.15rem',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
  btnSecondary: {
    background: 'transparent',
    color: '#FAF7F2',
    border: '1px solid #3A3A3A',
    padding: '0.55rem 1.15rem',
    borderRadius: '6px',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
  viewToggleGroup: {
    display: 'flex',
    background: '#141414',
    border: '1px solid #2C2C2C',
    borderRadius: '6px',
    padding: '2px',
  },
  viewToggleBtn: {
    background: 'transparent',
    border: 'none',
    color: '#777',
    padding: '0.35rem 0.65rem',
    fontSize: '0.75rem',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    cursor: 'pointer',
  },
  viewToggleActive: {
    background: '#2A2A2A',
    color: '#FAF7F2',
    fontWeight: 600,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: '0.75rem',
  },
  statCard: {
    background: '#191919',
    border: '1px solid #282828',
    borderRadius: '8px',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  statLabel: { fontSize: '0.75rem', color: '#888', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' },
  statValue: { fontSize: '1.75rem', fontWeight: 700, color: '#FAF7F2' },
  statSub: { fontSize: '0.75rem', color: '#666' },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  searchBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    background: '#161616',
    border: '1px solid #2C2C2C',
    borderRadius: '6px',
    padding: '0.5rem 0.75rem',
    gap: '0.5rem',
    flex: 1,
    minWidth: '220px',
    maxWidth: '100%',
  },
  searchInput: {
    background: 'transparent',
    border: 'none',
    color: '#FAF7F2',
    fontSize: '0.875rem',
    outline: 'none',
    width: '100%',
  },
  clearSearchBtn: {
    background: 'transparent',
    border: 'none',
    color: '#888',
    cursor: 'pointer',
    padding: 2,
    display: 'inline-flex',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem',
    gap: '0.75rem',
  },
  emptyState: {
    background: '#181818',
    border: '1px dashed #333',
    borderRadius: '10px',
    padding: '4rem 2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '1rem',
  },
  emptyTitle: { fontSize: '1.25rem', color: '#FAF7F2', margin: 0, fontWeight: 600 },
  emptySubtitle: { fontSize: '0.875rem', color: '#888', maxWidth: '420px', margin: 0, lineHeight: 1.5 },
  // Tree Styles
  treeContainer: {
    background: '#181818',
    border: '1px solid #2C2C2C',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  treeHeader: {
    padding: '0.875rem 1.25rem',
    background: '#121212',
    borderBottom: '1px solid #2C2C2C',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: '#9B9B9B',
    fontSize: '0.8125rem',
    fontWeight: 600,
  },
  treeList: { display: 'flex', flexDirection: 'column' },
  treeNodeGroup: { display: 'flex', flexDirection: 'column', borderBottom: '1px solid #242424' },
  treeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.85rem 1.25rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
    transition: 'background 0.15s ease',
  },
  treeRowLeft: { display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: '200px' },
  collapseToggle: {
    background: 'transparent',
    border: 'none',
    color: '#FAF7F2',
    cursor: 'pointer',
    padding: '0.2rem',
    display: 'inline-flex',
    alignItems: 'center',
  },
  catThumb: { width: 34, height: 34, borderRadius: 4, objectFit: 'cover', border: '1px solid #333' },
  catThumbPlaceholder: {
    width: 34,
    height: 34,
    borderRadius: 4,
    background: '#242424',
    border: '1px solid #333',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  treeTitleBlock: { display: 'flex', flexDirection: 'column', gap: '0.15rem' },
  treeTitleText: { color: '#FAF7F2', fontWeight: 600, fontSize: '0.9rem' },
  treeHandleText: { color: '#777', fontSize: '0.75rem', fontFamily: 'monospace' },
  treeRowRight: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  productCountBadge: {
    background: '#262626',
    border: '1px solid #333',
    color: '#C9A84C',
    padding: '0.2rem 0.55rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  treeActions: { display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' },
  treeActionBtn: {
    background: 'rgba(201, 168, 76, 0.12)',
    border: '1px solid rgba(201, 168, 76, 0.4)',
    color: '#C9A84C',
    borderRadius: '4px',
    padding: '0.3rem 0.6rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    cursor: 'pointer',
  },
  treeChildren: { display: 'flex', flexDirection: 'column' },
  // Badges
  badgeL1: { background: 'rgba(201, 168, 76, 0.2)', color: '#F3D27A', border: '1px solid #C9A84C', padding: '0.1rem 0.4rem', borderRadius: 3, fontSize: '0.68rem', fontWeight: 600 },
  badgeL2: { background: 'rgba(59, 130, 246, 0.2)', color: '#93C5FD', border: '1px solid #3B82F6', padding: '0.1rem 0.4rem', borderRadius: 3, fontSize: '0.68rem', fontWeight: 600 },
  badgeL3: { background: 'rgba(168, 85, 247, 0.2)', color: '#D8B4FE', border: '1px solid #A855F7', padding: '0.1rem 0.4rem', borderRadius: 3, fontSize: '0.68rem', fontWeight: 600 },
  // Table Styles
  tableCard: {
    background: '#181818',
    border: '1px solid #2C2C2C',
    borderRadius: '8px',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    maxWidth: '100%',
  },
  table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.875rem' },
  th: { padding: '0.875rem 1.25rem', background: '#121212', color: '#9B9B9B', fontWeight: 500, borderBottom: '1px solid #2C2C2C' },
  thRight: { padding: '0.875rem 1.25rem', background: '#121212', color: '#9B9B9B', fontWeight: 500, textAlign: 'right', borderBottom: '1px solid #2C2C2C' },
  tr: { borderBottom: '1px solid #262626' },
  td: { padding: '0.9rem 1.25rem', color: '#FAF7F2' },
  tdMono: { padding: '0.9rem 1.25rem', color: '#9B9B9B', fontFamily: 'monospace', fontSize: '0.8125rem' },
  tdMuted: { padding: '0.9rem 1.25rem', color: '#777' },
  tdRight: { padding: '0.9rem 1.25rem', textAlign: 'right' },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    color: '#9B9B9B',
    cursor: 'pointer',
    padding: '0.35rem',
    borderRadius: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    background: '#1A1A1A',
    border: '1px solid #333',
    borderRadius: '10px',
    padding: '1.5rem',
    width: '95vw',
    maxWidth: '560px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.25rem',
  },
  modalTitle: { margin: 0, color: '#FAF7F2', fontSize: '1.25rem', fontWeight: 600 },
  breadcrumbPreview: {
    marginTop: '0.35rem',
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  modalCloseBtn: {
    background: 'transparent',
    border: 'none',
    color: '#888',
    cursor: 'pointer',
    padding: '0.25rem',
  },
  errorAlert: {
    background: 'rgba(239, 68, 68, 0.15)',
    border: '1px solid #EF4444',
    color: '#FCA5A5',
    padding: '0.75rem 1rem',
    borderRadius: '6px',
    fontSize: '0.8125rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '1.15rem' },
  formRow: { display: 'flex', flexWrap: 'wrap', gap: '1rem' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '0.35rem', flex: 1, minWidth: '220px' },
  label: { fontSize: '0.78rem', color: '#C0C0C0', fontWeight: 500 },
  helperText: { fontSize: '0.72rem', color: '#777', lineHeight: 1.4 },
  input: {
    background: '#121212',
    border: '1px solid #333',
    borderRadius: '5px',
    padding: '0.65rem 0.8rem',
    color: '#FAF7F2',
    fontSize: '0.875rem',
    outline: 'none',
  },
  select: {
    background: '#121212',
    border: '1px solid #333',
    borderRadius: '5px',
    padding: '0.65rem 0.8rem',
    color: '#FAF7F2',
    fontSize: '0.875rem',
    outline: 'none',
  },
  textarea: {
    background: '#121212',
    border: '1px solid #333',
    borderRadius: '5px',
    padding: '0.65rem 0.8rem',
    color: '#FAF7F2',
    fontSize: '0.875rem',
    outline: 'none',
    resize: 'vertical',
  },
  imagePreviewBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem',
    background: '#141414',
    borderRadius: '5px',
    border: '1px solid #282828',
  },
  imagePreview: {
    width: 48,
    height: 48,
    objectFit: 'cover',
    borderRadius: 4,
    border: '1px solid #333',
  },
  seoSection: {
    background: '#141414',
    border: '1px solid #282828',
    borderRadius: '6px',
    padding: '0.85rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  seoTitleLabel: { fontSize: '0.75rem', color: '#C9A84C', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
  modalButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    marginTop: '0.5rem',
    borderTop: '1px solid #282828',
    paddingTop: '1rem',
  },
};
